import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import Grid from "@mui/material/Grid";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useAuth } from "../../../context/AuthContext";
import { Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Property,
  PropertyListing,
  AssigneeTeamMember,
} from "../../../types/Property";
import { useProperty } from "../../../context/PropertyContext";
import { Close, Notifications } from "@mui/icons-material";
import PostModal from "../../../components/modals/PostModal";
import Swal from "sweetalert2";
import CustomAppBar from "../../../components/shared/CustomAppBar";
import { getTeamMembersByLeaderId } from "../../../services/realtorService";
import { useCompany } from "../../../context/CompanyContext";

const PublishPropertyPage = () => {
  const { name, userToken } = useAuth();
  const navigate = useNavigate();
  const { pageUrl } = useParams(); 
  const location = useLocation();
  const {
    userPropertyPosts,
    fetchUserPropertyPosts,
    deleteProperty,
    handleSetSelectedProperty,
  } = useProperty();
  const [selectedPost, setSelectedPost] = useState<PropertyListing | null>(
    null
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [scrolled, setScrolled] = useState(false);
  const [propertyCategory, setPropertyCategory] = useState<string>("");
  const [propertyType, setPropertyType] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [addressDetails, setAddressDetails] = useState({
    country: "",
    nearbyLandmark: "",
    streetAddress: "",
    province: "",
    county: "",
    district: "",
    neighborhood: "",
    postalCode: "",
    apartment: "",
    googleMapsUrl: "",
    latitude: "",
    longitude: "",
    Zone: "",
  });
  const [propertyDescription, setPropertyDescription] = useState<string>("");
  const [propertyServices, setPropertyServices] = useState<string>("");
  const [propertyAmenities, setPropertyAmenities] = useState<string>("");
  const [propertyTitle, setPropertyTitle] = useState<string>("");
  const [propertyPrice, setPropertyPrice] = useState<string>("123445");
  const [propertyCurrency, setPropertyCurrency] = useState<string>("CRC");
  const [propertyDimension, setPropertyDimension] = useState<number>(0);
  const [propertyData, setPropertyData] = useState<Record<string, any>>({});
  const [propertyVisibility, setPropertyVisibility] = useState<string>("");
  const [propertyHunter, setPropertyHunter] = useState<number | null>(null);
  const [propertyCounty, setPropertyCounty] = useState<string>("");
  const [propertyDistrict, setPropertyDistrict] = useState<string>("");
  const [commission, setCommission] = useState<string>("");
  const [commissionType, setCommissionType] = useState(1);
  const [property, setProperty] = useState<Property>({
    code: "",
    title: "",
    category: "",
    type: "",
    dimension: 0,
    details: "",
    description: "",
    services: "",
    amenities: "",
    price: 0,
    owner: "",
    addressDescription: "",
    photos: [],
    county: "",
    district: "",
    visibility: "",
    hunter: 0,
    currency: "",
    commision: "",
    commisionType: 1,
  });

  const [teamMembers, setTeamMembers] = useState<AssigneeTeamMember[]>([]);
  const [selectedRealtorId, setSelectedRealtorId] = useState<number | null>(
    null
  );
  const [currentRealtorId, setCurrentRealtorId] = useState<number | null>(null);
  const [, setLoadingTeamMembers] = useState<boolean>(false);

  const { company } = useCompany();

  useEffect(() => {
    if (selectedRealtorId) {
      localStorage.setItem("selectedRealtorId", selectedRealtorId.toString());
    }
  }, [selectedRealtorId]);

  const displayedPosts = userPropertyPosts.slice(0, 3);


  const handleRowClick = (propertyPost: PropertyListing) => {
    setSelectedPost(propertyPost);
    setPostModalOpen(true);
  };

  const handleClosePostModal = () => {
    setPostModalOpen(false);
    setSelectedPost(null);
  };

  const handleEdit = () => {
    handleSetSelectedProperty(selectedPost);
    navigate(`/properties/edit-property/${selectedPost!.title}`);
    handleCloseModal();
  };

  const handleDelete = async () => {
    handleClosePostModal();
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la propiedad y todas sus fotos asociadas.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed && selectedPost) {
      handleCloseModal();
      try {
        const formData = new FormData();
        formData.append("PropertyId", selectedPost.id_Property.toString());
        const deleteResult = await deleteProperty(formData);
        if (deleteResult) {
          Swal.fire("¡Eliminado!", "Propiedad eliminada", "success");
        } else {
          Swal.fire("Error", "error al eliminar", "error");
        }
      } catch (error) {
        Swal.fire(
          "Error",
          "Ocurrió un error al eliminar la propiedad.",
          "error"
        );
      }
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectPublication = (selectedPost: any) => {
    setPropertyTitle(selectedPost.title || "");
    setPropertyCategory(selectedPost.categoryName || "");
    setPropertyType(selectedPost.modeDescription || "");
    setPropertyDimension(selectedPost.dimension || 0);
    setPropertyDescription(selectedPost.description || "");
    setPropertyServices(selectedPost.services || "");
    setPropertyAmenities(selectedPost.amenities || "");
    setPropertyPrice(selectedPost.hunter || 0);
    setPropertyPrice(selectedPost.price || 0);
    setCommission(selectedPost.commission || "");
    setCommissionType(selectedPost.commisionType || 1);

    const data = {
      propertyCategory,
      propertyType,
      propertyServices,
      propertyAmenities,
      propertyTitle,
      propertyDescription,
      propertyPrice,
      propertyDimension,
      propertyData,
      propertyVisibility,
      propertyHunter,
      commission,
      commissionType,
    };

    localStorage.setItem("propertyData", JSON.stringify(data));

    navigate("overview");
    handleCloseModal();
  };

  const handleShowAll = () => {
    navigate("/properties/user/posts");
  };

  const handleExit = () => {
  if (pageUrl) {
    window.location.assign(`/company/${pageUrl}/properties/home`);
  } else {
    window.location.assign("/properties/home");
  }
};


  const handleNavigate = () => {
    setPropertyTitle("");
    setPropertyCategory("");
    setPropertyType("");
    setPropertyDimension(0);
    setPropertyDescription("");
    setPropertyServices("");
    setPropertyAmenities("");
    setPropertyPrice("");
    setCommission("");
    setCommissionType(1);
    setAddressDetails({
      country: "",
      nearbyLandmark: "",
      streetAddress: "",
      province: "",
      county: "",
      district: "",
      neighborhood: "",
      postalCode: "",
      apartment: "",
      googleMapsUrl: "",
      latitude: "",
      longitude: "",
      Zone: "",
    });
    setPropertyData({});
    setPropertyVisibility("");
    setPropertyCurrency("CRC");

    localStorage.removeItem("propertyData");
    localStorage.removeItem("uploadedPhotos");

    navigate("overview");
  };

  const handleNavigateToStructure = () => {
    navigate("structure");
  };

  const handleNavigateToType = () => {
    navigate("type");
  };

  const handleNavigateToLocation = () => {
    navigate("location");
  };

  const handleNavigateToConfirmLocation = () => {
    navigate("confirm-location");
  };

  const handleNavigateToPropertyData = () => {
    navigate("property-data");
  };

  const handleNavigateToPropertyServices = () => {
    navigate("service");
  };

  const handleNavigateToPropertyAmenities = () => {
    navigate("amenitie");
  };

  const handleNavigateToPropertyUploadPhotos = () => {
    navigate("upload-photos");
  };

  const handleNavigateToPropertyTitle = () => {
    navigate("title");
  };

  const handleNavigateToPropertyDescription = () => {
    navigate("description");
  };

  const handleNavigateToPropertyVisibility = () => {
    navigate("visibility");
  };

  const handleNavigateToPropertyHunter = () => {
    navigate("hunter");
  };

  const handleNavigateToPropertyAssignee = () => {
    navigate("assignee");
  };

  const handleNavigateToPropertyPrice = () => {
    navigate("price");
  };

  const handleNavigateToRealtorSelection = () => {
    navigate("realtor-selection");
  };

  const handleNavigateToPropertyResume = () => {
    navigate("resume");
  };

  const handleNavigateToFinish = () => {
    navigate("finish");
  };

  const handleNavigateToCommissionPage = () => {
    navigate("commission");
  };

  useEffect(() => {
    const savedData = localStorage.getItem("propertyData");

    if (savedData) {
      const data = JSON.parse(savedData);
      setPropertyCategory(data.propertyCategory || "");
      setPropertyType(data.propertyType || "");
      setAddressDetails(data.addressDetails || {});
      setPropertyServices(data.propertyServices || "");
      setPropertyAmenities(data.propertyAmenities || "");
      setPropertyTitle(data.propertyTitle || "");
      setPropertyDescription(data.propertyDescription || "");
      setPropertyPrice(data.propertyPrice || "");
      setPropertyDimension(data.propertyDimension || 0);
      setPropertyData(data.propertyData || {});
      setPropertyVisibility(data.propertyVisibility || "");
      setPropertyHunter(data.propertyHunter || "");
      setPropertyCounty(data.propertyCounty || "");
      setPropertyDistrict(data.propertyDistrict || "");
      setCommission(data.commission || "");
      setCommissionType(data.commissionType || 1);

      const addressDescription = [
        data.addressDetails?.streetAddress || "",
        data.addressDetails?.apartment
          ? `Apto ${data.addressDetails.apartment}`
          : "",
        data.addressDetails?.district || "",
        data.addressDetails?.postalCode || "",
        data.addressDetails?.province || "",
        data.addressDetails?.neighborhood || "",
        data.addressDetails?.nearbyLandmark || "",
        data.addressDetails?.country || "",
      ]
        .filter(Boolean)
        .join(", ");

      setProperty({
        code: data.propertyCode || "",
        title: data.propertyTitle || "",
        category: data.propertyCategory || "",
        type: data.propertyType || "",
        details: data.propertyData || {},
        dimension: data.propertyDimension || 0,
        description: data.propertyDescription || "",
        services: data.propertyServices || "",
        amenities: data.propertyAmenities || "",
        price: data.propertyPrice || 0,
        owner: name || "",
        addressDescription: addressDescription,
        photos: [],
        county: data.county || "",
        district: data.district || "",
        visibility: data.propertyVisibility || "",
        hunter: data.propertyHunter || "",
        currency: data.currency || "",
        commision: data.commission,
        commisionType: data.commissionType,
      });
    }
  }, [name]);

  useEffect(() => {
    const addressDescription = [
      addressDetails.streetAddress || "",
      addressDetails.apartment ? `Apto ${addressDetails.apartment}` : "",
      addressDetails.district || "",
      addressDetails.postalCode || "",
      addressDetails.province || "",
      addressDetails.neighborhood || "",
      addressDetails.nearbyLandmark || "",
      addressDetails.country || "",
    ]
      .filter(Boolean)
      .join(", ");

    setProperty({
      code: "",
      title: propertyTitle,
      category: propertyCategory,
      type: propertyType,
      dimension: propertyDimension,
      description: propertyDescription,
      details: JSON.stringify(propertyData),
      services: propertyServices,
      amenities: propertyAmenities,
      price: Number(propertyPrice),
      owner: name || "",
      addressDescription: addressDescription,
      photos: [],
      county: propertyCounty,
      district: propertyDistrict,
      visibility: propertyVisibility,
      hunter: propertyHunter !== null ? propertyHunter : 0,
      currency: propertyCurrency,
      commision: commission,
      commisionType: commissionType,
    });
  }, [
    propertyCategory,
    propertyType,
    addressDetails,
    propertyServices,
    propertyAmenities,
    propertyTitle,
    propertyData,
    propertyPrice,
    propertyDimension,
    name,
    propertyDescription,
    propertyCounty,
    propertyDistrict,
    propertyVisibility,
    propertyHunter,
    propertyCurrency,
    commission,
    commissionType,
  ]);

  useEffect(() => {
    const data = {
      propertyCategory,
      propertyType,
      addressDetails,
      propertyServices,
      propertyAmenities,
      propertyTitle,
      propertyDescription,
      propertyPrice,
      propertyDimension,
      propertyData,
      propertyVisibility,
      propertyHunter,
      propertyCurrency,
      commission,
      commissionType,
    };
    localStorage.setItem("propertyData", JSON.stringify(data));
  }, [
    propertyCategory,
    propertyType,
    addressDetails,
    propertyServices,
    propertyAmenities,
    propertyTitle,
    propertyPrice,
    propertyDimension,
    propertyData,
    propertyVisibility,
    propertyHunter,
    propertyDescription,
    propertyCurrency,
    commission,
    commissionType,
  ]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        await fetchUserPropertyPosts(userToken!.user.id, company?.id);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();

    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      setLoadingTeamMembers(true);

      try {
        const realtorProfile = userToken?.user.profiles.find(
          (profile) => profile.roleId === 3
        );

        const isLeader = realtorProfile?.realtor?.isLeader;

        const realtorId = realtorProfile?.realtor?.id || null;

        setCurrentRealtorId(realtorId);

        if (isLeader && realtorId && userToken) {
          try {
            const apiMembers = await getTeamMembersByLeaderId(
              realtorId.toString(),
              userToken.hashedKey
            );

            if (Array.isArray(apiMembers) && apiMembers.length > 0) {
              const teamMembersList: AssigneeTeamMember[] = apiMembers.map(
                (member) => ({
                  id_Realtor: member.idRealtor,
                  fullName: member.fullName || `Miembro ${member.idRealtor}`,
                  profileImage: member.profileImage || null,
                  isLeader: member.isLeader || false,
                })
              );

              setTeamMembers(teamMembersList);
            } else {
              console.warn(
                "No se recibieron miembros del equipo o el formato es incorrecto:",
                apiMembers
              );
              if (realtorId) {
                setTeamMembers([
                  {
                    id_Realtor: realtorId,
                    fullName: userToken.user.fullName || "Tú (Actual)",
                    profileImage: userToken.user.photo || null,
                    isLeader: true,
                  },
                ]);
              }
            }
          } catch (apiError) {
            console.error("Error fetching team members from API:", apiError);
            if (realtorId) {
              setTeamMembers([
                {
                  id_Realtor: realtorId,
                  fullName: userToken.user.fullName || "Tú (Actual)",
                  profileImage: userToken.user.photo || null,
                  isLeader: true,
                },
              ]);
            }
          }
        } else if (realtorId) {
          setTeamMembers([
            {
              id_Realtor: realtorId,
              fullName: userToken?.user.fullName || "Tú (Actual)",
              profileImage: userToken?.user.photo || null,
              isLeader: realtorProfile?.realtor?.isLeader || false,
            },
          ]);
        }
      } catch (error) {
        console.error("Error general en fetchTeamMembers:", error);
      } finally {
        setLoadingTeamMembers(false);
      }
    };

    if (userToken) {
      fetchTeamMembers();
    }
  }, [userToken]);

  const isChildRoute = location.pathname.includes("/publish-property") && !location.pathname.endsWith("/publish-property");
  const isFinishRoute = location.pathname.endsWith("/finish");
  const [isLoading, setIsLoading] = useState(true);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Barra de navegación */}
      {!isFinishRoute && <CustomAppBar />}

      {/* Cuerpo de la página */}
      <Box
        sx={{
          mt: isFinishRoute ? "0" : "10vh",
          px: isFinishRoute ? 0 : 2,
          pb: { xs: 2 },
          flexGrow: isFinishRoute ? 0 : 1,
          bgcolor: "white",
        }}
      >
        {!isChildRoute && (
          <>
            <Typography variant="h4" sx={{ mt: 4 }} gutterBottom>
              Hola de nuevo, {name}
            </Typography>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Aquí puedes gestionar tus publicaciones y crear nuevas.
            </Typography>

            {/* Secciones en columnas */}
            <Grid container spacing={4} sx={{ mt: 4 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Publicaciones Existentes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {isLoading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      sx={{ minHeight: 200 }}
                    >
                      <CircularProgress sx={{ color: "green" }} />
                    </Box>
                  ) : displayedPosts.length === 0 ? (
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      textAlign="center"
                    >
                      No tienes publicaciones existentes.
                    </Typography>
                  ) : (
                    <List>
                      {displayedPosts.map((propertyPost) => (
                        <ListItemButton
                          key={propertyPost.id_Property}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: 2,
                          }}
                          onClick={() => handleRowClick(propertyPost)}
                        >
                          <>
                            <Box
                              component="img"
                              src={propertyPost.photos[0]}
                              alt={propertyPost.title}
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 1,
                                objectFit: "cover",
                                mr: 2,
                              }}
                            />
                            <ListItemText
                              primary={propertyPost.title}
                              secondary={
                                <>
                                  {propertyPost.createdDate
                                    ? `Publicación creada el ${new Date(
                                        propertyPost.createdDate
                                      ).toLocaleDateString("es-ES", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                      })}`
                                    : "Fecha no válida"}

                                  {propertyPost.collaborationRequests &&
                                    propertyPost.collaborationRequests?.filter(
                                      (req) => req.status === 0
                                    ).length > 0 && (
                                      <Box
                                        sx={{
                                          mt: 1,
                                          display: "flex",
                                          alignItems: "center",
                                          color: "#ffa726",
                                          fontSize: "0.8rem",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Notifications sx={{ fontSize: 18 }} />
                                        <Typography
                                          variant="caption"
                                          sx={{ fontWeight: 500 }}
                                        >
                                          (
                                          {
                                            propertyPost.collaborationRequests.filter(
                                              (req) => req.status === 0
                                            ).length
                                          }
                                          ) solicitudes de colaboración
                                          pendientes
                                        </Typography>
                                      </Box>
                                    )}
                                </>
                              }
                            />
                          </>
                        </ListItemButton>
                      ))}
                    </List>
                  )}

                  {/* Mostrar más o menos publicaciones */}
                  {!isLoading && (
                    <Box textAlign="end">
                      <Button
                        variant="text"
                        sx={{ color: "green" }}
                        onClick={() => handleShowAll()}
                      >
                        Mostrar todo
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Sección para crear nueva publicación */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Crear Nueva Publicación
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {/* Aquí se puede implementar el formulario para crear una nueva publicación */}
                  <List>
                    <ListItem secondaryAction={<ArrowForwardIosIcon />}>
                      <ListItemButton onClick={() => handleNavigate()}>
                        <ListItemIcon>
                          <AddCircleIcon />
                        </ListItemIcon>
                        <ListItemText primary="Crear Nueva Publicación" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem secondaryAction={<ArrowForwardIosIcon />}>
                      <ListItemButton onClick={handleOpenModal}>
                        <ListItemIcon>
                          <ListAltIcon />
                        </ListItemIcon>
                        <ListItemText primary="Crear a partir de Publicación Existente" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                  {/* Modal con todas las publicaciones */}
                  <Dialog
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogTitle
                      sx={{ textAlign: "center", fontWeight: "bold" }}
                    >
                      Selecciona una publicación existente
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 8,
                          color: "text.primary",
                          zIndex: 1,
                        }}
                        onClick={handleCloseModal}
                      >
                        <Close />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent>
                      {userPropertyPosts.length === 0 ? (
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          textAlign="center"
                        >
                          No tienes publicaciones existentes para seleccionar.
                        </Typography>
                      ) : (
                        <List>
                          {userPropertyPosts.map((propertyPost) => (
                            <ListItemButton
                              key={propertyPost.id_Property}
                              onClick={() =>
                                handleSelectPublication(propertyPost)
                              }
                              sx={{
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                mb: 2,
                                transition: "background-color 0.3s, color 0.3s",
                                "&:hover": {
                                  border: "2px solid black",
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src={propertyPost.photos[0]}
                                alt={propertyPost.title}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 1,
                                  objectFit: "cover",
                                  mr: 2,
                                }}
                              />
                              <ListItemText
                                primary={propertyPost.title}
                                secondary={
                                  propertyPost.createdDate
                                    ? `Publicación creada el ${new Date(
                                        propertyPost.createdDate
                                      ).toLocaleDateString("es-ES", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                      })}`
                                    : "Fecha no válida"
                                }
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      )}
                    </DialogContent>
                  </Dialog>
                </Box>
              </Grid>
            </Grid>
            {selectedPost && (
              <PostModal
                open={postModalOpen}
                onClose={handleClosePostModal}
                post={selectedPost}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            )}
          </>
        )}
        <Outlet
          context={{
            propertyCategory,
            setPropertyCategory,
            propertyType,
            setPropertyType,
            addressDetails,
            setAddressDetails,
            propertyServices,
            setPropertyServices,
            propertyAmenities,
            setPropertyAmenities,
            propertyTitle,
            setPropertyTitle,
            propertyDescription,
            setPropertyDescription,
            propertyCounty,
            setPropertyCounty,
            propertyDistrict,
            setPropertyDistrict,
            propertyPrice,
            setPropertyPrice,
            propertyCurrency,
            setPropertyCurrency,
            propertyDimension,
            setPropertyDimension,
            propertyData,
            setPropertyData,
            propertyVisibility,
            setPropertyVisibility,
            setPropertyHunter,
            property,
            teamMembers,
            selectedRealtorId,
            setSelectedRealtorId,
            currentRealtorId,
            commission,
            setCommission,
            commissionType,
            setCommissionType,
            handleNavigateToCommissionPage,
            handleNavigateToStructure,
            handleNavigateToType,
            handleNavigateToLocation,
            handleNavigateToConfirmLocation,
            handleNavigateToPropertyData,
            handleNavigateToPropertyServices,
            handleNavigateToPropertyAmenities,
            handleNavigateToPropertyUploadPhotos,
            handleNavigateToPropertyTitle,
            handleNavigateToPropertyDescription,
            handleNavigateToPropertyAssignee,
            handleNavigateToPropertyVisibility,
            handleNavigateToPropertyHunter,
            handleNavigateToPropertyPrice,
            handleNavigateToRealtorSelection,
            handleNavigateToPropertyResume,
            handleNavigateToFinish,
            handleExit,
          }}
        />
        <Box
          sx={{
            height: "80px",
            display: { xs: "block", sm: "none" },
            width: "100%",
          }}
        />
      </Box>
    </Box>
  );
};

export default PublishPropertyPage;
